import { Assessment, DownloadOutlined, Timeline } from '@mui/icons-material'
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt'
import {
  Typography,
  Box,
  TableContainer,
  Table,
  Paper,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  TablePagination,
  CircularProgress,
  IconButton,
  Button,
  Link,
  Theme,
  Tabs,
  Tab,
} from '@mui/material'
import { Role } from 'core/constants/enum'
import Path from 'core/navigation/path'
import { setReportsDataAction } from 'features/reports/reducers/reports'
import { getVarianceCellColor, hoursToHMFormat } from 'features/reports/utils'
import { getAllUsers } from 'features/users-management/thunks/users-management'
import { identity } from 'lodash'
import moment from 'moment'
import React, { useState, useEffect } from 'react'
import { CSVLink } from 'react-csv'
import { Navigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import HolidayModal from '../../settings/components/HolidayModal'
import RequestModal from '../../work-request/components/RequestModal'
import { FilterReport } from '../components/FilterReport'
import LocationReportDisplay, {
  LocationSummary,
} from '../components/LocationReportDisplay'
import LocationReportFilter from '../components/LocationReportFilter'
import TimesheetFilter from '../components/TimesheetFilter'
import UserActivityFeedView from '../components/UserActivityFeedView'
import { reportService } from '../service/service'

const EmptyDevLogCell = () => {
  const { isDarkMode } = useAppSelector(state => state.app)
  return <TableCell sx={themeStyles(isDarkMode).timesheetCell} />
}

export type HeaderProps = {
  name: string
  subText?: string
}
const WhoisinTableHeader: React.FC<HeaderProps> = ({ name, subText }) => {
  const { isDarkMode } = useAppSelector(state => state.app)
  return (
    <TableCell sx={themeStyles(isDarkMode).tableHeader}>
      <Box component="span">
        <Typography sx={styles.headerText}>{name}</Typography>
        {subText && (
          <Typography sx={styles.headerSubText}>{subText}</Typography>
        )}
      </Box>
    </TableCell>
  )
}

WhoisinTableHeader.defaultProps = {
  subText: '',
}

const DevLogsTableHeader: React.FC<HeaderProps> = ({ name, subText }) => {
  const { isDarkMode } = useAppSelector(state => state.app)
  return (
    <TableCell sx={themeStyles(isDarkMode).tableHeaderLogs}>
      <Box component="span">
        <Typography sx={styles.headerText}>{name}</Typography>
        {subText && (
          <Typography sx={styles.headerSubText}>{subText}</Typography>
        )}
      </Box>
    </TableCell>
  )
}

DevLogsTableHeader.defaultProps = {
  subText: '',
}

export type FormattedCell = {
  hours: number | undefined
}

const HM_FormattedCell: React.FC<FormattedCell> = ({ hours }) => {
  const { isDarkMode } = useAppSelector(state => state.app)
  return (
    <TableCell sx={themeStyles(isDarkMode).timesheetCell}>
      {hours && `${hoursToHMFormat(hours)} (${hours})`}
    </TableCell>
  )
}

const Deviation_FormattedCell: React.FC<FormattedCell> = ({ hours }) => {
  const { isDarkMode } = useAppSelector(state => state.app)
  return (
    <TableCell sx={getVarianceCellColor(isDarkMode, hours)}>
      {`${hoursToHMFormat(hours ?? 0)} (${hours ?? 0})`}
    </TableCell>
  )
}

const Reports = () => {
  const dispatch = useAppDispatch()
  const { isDarkMode } = useAppSelector(state => state.app)
  const { user } = useAppSelector(state => state.auth)
  const { reportsData, isLoading } = useAppSelector(state => state.reports)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [openModal, setOpenModal] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [locationReportData, setLocationReportData] = useState<
    LocationSummary[]
  >([])
  const [locationReportLoading, setLocationReportLoading] = useState(false)
  const [, setLocationReportDateRange] = useState('')

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const [activityFeedModal, setActivityFeedModal] = useState({
    isOpen: false,
    userId: '',
    userName: '',
    dateStr: '',
  })

  const handleCloseModal = () => {
    setOpenModal(false)
  }

  const handleCloseActivityFeedModal = () => {
    setActivityFeedModal({
      isOpen: false,
      userId: '',
      userName: '',
      dateStr: '',
    })
  }

  const handleDateClick = (
    userId: string,
    userName: string,
    dateStr: string,
  ) => {
    setActivityFeedModal({
      isOpen: true,
      userId,
      userName,
      dateStr,
    })
  }

  useEffect(() => {
    dispatch(setReportsDataAction([]))
    if (user?.role === Role.Admin) {
      dispatch(getAllUsers(user.role))
    }
  }, [dispatch, user?.role])

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // Format report data for CSV export, adding employee information
  const getFormattedExportData = () =>
    reportsData.map(row => ({
      ...row,
      // Add a combined employee field for export
      employee: row.employeeNo
        ? `${row.name || ''}\n${row.employeeNo}`
        : row.name || '',
    }))

  const tableHeader = [
    { label: 'Date', key: 'date' },
    { label: 'Employee', key: 'employee' },
    { label: 'Shift', key: 'shift' },
    { label: 'Work Location', key: 'wfh' },
    { label: 'Total Time', key: 'totalDuration' },
    { label: 'Break', key: 'breakDuration' },
    { label: 'Net Hours', key: 'workDurationInHrs' },
    { label: 'Dev Log (hrs)', key: 'devlogHours' },
    { label: 'Leave (hrs)', key: 'leaveHours' },
    { label: 'Training (hrs)', key: 'trainingHours' },
    { label: 'Billable (hrs)', key: 'billableHours' },
    { label: 'Non-Billable (hrs)', key: 'nonBillableHours' },
    { label: 'Outage (hrs)', key: 'outageHours' },
    { label: 'Net (hrs)', key: 'netDevlogHrs' },
    { label: 'Deviation (hrs)', key: 'deviationHours' },
  ]

  const handleLocationReport = async (fromDate: number, toDate: number) => {
    setLocationReportLoading(true)
    const dateRangeStr = `${moment(fromDate).format('MM/DD/YY')} - ${moment(
      toDate,
    ).format('MM/DD/YY')}`
    setLocationReportDateRange(dateRangeStr)
    try {
      const locationData = await reportService.getLocationReport(
        fromDate,
        toDate,
      )
      setLocationReportData(locationData)
    } catch (error) {
      console.error('Error fetching location report:', error)
    } finally {
      setLocationReportLoading(false)
    }
  }

  return user?.role === Role.Admin || user?.role === Role.HR ? (
    <Box sx={styles.root}>
      <Box sx={styles.header}>
        <Box alignItems="center" display="flex" gap={2}>
          <Assessment color="action" fontSize="large" />
          <Typography variant="h4">Reports</Typography>
        </Box>
        <Button
          startIcon={<SystemUpdateAltIcon />}
          variant="contained"
          onClick={() => setOpenModal(true)}
        >
          Import Timesheet Data
        </Button>

        <HolidayModal
          Component={<TimesheetFilter onClose={handleCloseModal} />}
          maxWidth="sm"
          open={openModal}
          title="Import Timesheet Data"
          onClose={handleCloseModal}
        />
      </Box>

      <RequestModal
        Component={
          <UserActivityFeedView
            dateStr={activityFeedModal.dateStr}
            userId={activityFeedModal.userId}
          />
        }
        open={activityFeedModal.isOpen}
        title={`Activity Feed for ${activityFeedModal.userName} - ${activityFeedModal.dateStr}`}
        onClose={handleCloseActivityFeedModal}
      />

      <Box sx={{ mt: 3, mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          indicatorColor="primary"
          textColor="primary"
          value={activeTab}
          onChange={handleTabChange}
        >
          <Tab label="Time Tracking Report" />
          <Tab label="Location Report" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <>
          <FilterReport resetPage={() => setPage(0)} />
          {!isLoading && reportsData.length > 0 && (
            <Box display={'flex'} m={2} mt={0}>
              <Typography ml="auto" variant="subtitle2">
                Download as excel file :&emsp;
              </Typography>
              <CSVLink
                data={getFormattedExportData()}
                filename={`Whoisin Report - ${new Date()
                  .toLocaleString()
                  .split(', ')
                  .join(' - ')}.csv`}
                headers={tableHeader}
              >
                <IconButton sx={styles.downloadIcon}>
                  <DownloadOutlined />
                </IconButton>
              </CSVLink>
            </Box>
          )}
          {isLoading ? (
            <Box sx={styles.loader}>
              <CircularProgress size={27} />
              <Typography>Loading...</Typography>
            </Box>
          ) : (
            <Box>
              {reportsData.length > 0 ? (
                <Paper sx={themeStyles(isDarkMode).table} variant="outlined">
                  <TableContainer sx={{ maxHeight: 'calc(100vh - 100px)' }}>
                    <Table
                      stickyHeader
                      sx={{
                        '& .MuiTableHead-root': {
                          position: 'sticky',
                          top: 0,
                          zIndex: 2,
                        },
                        '& .MuiTableHead-root .MuiTableRow-root': {
                          position: 'sticky',
                          zIndex: 2,
                        },
                        '& .MuiTableHead-root .MuiTableRow-root:nth-of-type(1)':
                          {
                            top: 0,
                          },
                        '& .MuiTableHead-root .MuiTableRow-root:nth-of-type(2)':
                          {
                            top: '57px', // Height of the first header row
                          },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell
                            align="center"
                            colSpan={7}
                            sx={{
                              backgroundColor: isDarkMode
                                ? 'rgba(81, 143, 175)'
                                : '#4fc3f7',
                              fontWeight: 'bold',
                              fontSize: '1rem',
                              borderRight: '2px solid',
                              borderRightColor: isDarkMode ? '#333' : '#e0e0e0',
                            }}
                          >
                            Whoisin Hours
                          </TableCell>
                          <TableCell
                            align="center"
                            colSpan={8}
                            sx={{
                              backgroundColor: isDarkMode
                                ? '#b0b42c'
                                : '#ffe57f',
                              fontWeight: 'bold',
                              fontSize: '1rem',
                            }}
                          >
                            Timesheet Hours
                          </TableCell>
                        </TableRow>
                        <TableRow sx={styles.headerRow}>
                          <WhoisinTableHeader name="Employee" />
                          <WhoisinTableHeader name="Date" />
                          <WhoisinTableHeader name="Shift" />
                          <WhoisinTableHeader name="Loc" />
                          <WhoisinTableHeader name="Total" />
                          <WhoisinTableHeader name="Break" />
                          <WhoisinTableHeader
                            name="Net Hours"
                            subText="(Total - Break)"
                          />
                          <DevLogsTableHeader name="Dev Log" />
                          <DevLogsTableHeader name="Leave" />
                          <DevLogsTableHeader name="Training" />
                          <DevLogsTableHeader name="Billable" />
                          <DevLogsTableHeader name="Non-Billable" />
                          <DevLogsTableHeader name="Outage" />
                          <DevLogsTableHeader
                            name="Net"
                            subText="(Total - Leave)"
                          />
                          <DevLogsTableHeader name="Deviation" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportsData
                          .slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          )
                          .map((row, index) => {
                            if (row.date && row.isWeekend) {
                              return (
                                <TableRow
                                  key={identity(
                                    `${row.date}-${row.shift}-${index}`,
                                  )}
                                  sx={themeStyles(isDarkMode).tableRowWeekday}
                                >
                                  <TableCell>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                      }}
                                    >
                                      <Typography sx={styles.employeeName}>
                                        {row.name}
                                      </Typography>
                                      <Typography sx={styles.employeeCol}>
                                        {row.employeeNo}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>{row.date}</TableCell>
                                  <TableCell>
                                    <Typography
                                      component="span"
                                      sx={styles.weekday}
                                    >
                                      {row.shift} &#x1F389;
                                    </Typography>
                                  </TableCell>
                                  <TableCell />
                                  <TableCell />
                                  <TableCell />
                                  <TableCell />
                                  <EmptyDevLogCell />
                                  <EmptyDevLogCell />
                                  <EmptyDevLogCell />
                                  <EmptyDevLogCell />
                                  <EmptyDevLogCell />
                                  <EmptyDevLogCell />
                                  <EmptyDevLogCell />
                                  <EmptyDevLogCell />
                                </TableRow>
                              )
                            } else if (row.date && row.isHoliday) {
                              return (
                                <TableRow
                                  key={identity(
                                    `${row.date}-${row.shift}-${index}`,
                                  )}
                                  sx={themeStyles(isDarkMode).tableRowHoliday}
                                >
                                  <TableCell>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                      }}
                                    >
                                      <Typography sx={styles.employeeCol}>
                                        {row.employeeNo}
                                      </Typography>
                                      <Typography sx={styles.employeeName}>
                                        {row.name}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>{row.date}</TableCell>
                                  <TableCell>
                                    <Typography
                                      component="span"
                                      sx={styles.weekday}
                                    >
                                      {row.shift}
                                    </Typography>
                                  </TableCell>
                                  <TableCell />
                                  <TableCell />
                                  <TableCell />
                                  <TableCell />
                                  <EmptyDevLogCell />
                                  <EmptyDevLogCell />
                                  <EmptyDevLogCell />
                                  <EmptyDevLogCell />
                                  <EmptyDevLogCell />
                                  <EmptyDevLogCell />
                                  <EmptyDevLogCell />
                                  <EmptyDevLogCell />
                                </TableRow>
                              )
                            } else if (
                              row.date &&
                              row.isLeave &&
                              !row.isLeavePending
                            ) {
                              return (
                                <TableRow
                                  key={`${row.name}-${row.date}`}
                                  sx={themeStyles(isDarkMode).tableRowLeave}
                                >
                                  <TableCell>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                      }}
                                    >
                                      <Typography sx={styles.employeeName}>
                                        {row.name}
                                      </Typography>
                                      <Typography sx={styles.employeeCol}>
                                        {row.employeeNo}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>{row.date}</TableCell>
                                  <TableCell>
                                    <Typography
                                      component="span"
                                      sx={styles.weekday}
                                    >
                                      {row.shift}
                                    </Typography>
                                  </TableCell>
                                  <TableCell />
                                  <TableCell />
                                  <TableCell />
                                  <TableCell />
                                  <HM_FormattedCell
                                    hours={row.devlogHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.leaveHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.trainingHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.billableHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.nonBillableHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.outageHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.netDevlogHrs ?? 0}
                                  />
                                  <Deviation_FormattedCell
                                    hours={row.deviationHours}
                                  />
                                </TableRow>
                              )
                            } else if (row.date && row.isLeavePending) {
                              return (
                                <TableRow
                                  key={`${row.name}-${row.date}`}
                                  sx={themeStyles(isDarkMode).rowPendingLeave}
                                >
                                  <TableCell>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                      }}
                                    >
                                      <Typography sx={styles.employeeName}>
                                        {row.name}
                                      </Typography>
                                      <Typography sx={styles.employeeCol}>
                                        {row.employeeNo}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>{row.date}</TableCell>
                                  <TableCell>
                                    <Typography
                                      component="span"
                                      sx={styles.weekday}
                                    >
                                      <Timeline />
                                      {row.shift}
                                    </Typography>
                                  </TableCell>
                                  <TableCell />
                                  <TableCell />
                                  <TableCell />
                                  <TableCell />
                                  <HM_FormattedCell
                                    hours={row.devlogHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.leaveHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.trainingHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.billableHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.nonBillableHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.outageHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.netDevlogHrs ?? 0}
                                  />
                                  <Deviation_FormattedCell
                                    hours={row.deviationHours}
                                  />
                                </TableRow>
                              )
                            } else if (row.date && row.isNoShift) {
                              return (
                                <TableRow
                                  key={`${row.name}-${row.date}`}
                                  sx={themeStyles(isDarkMode).tableRowShift}
                                >
                                  <TableCell>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                      }}
                                    >
                                      <Typography sx={styles.employeeName}>
                                        {row.name}
                                      </Typography>
                                      <Typography sx={styles.employeeCol}>
                                        {row.employeeNo}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>{row.date}</TableCell>
                                  <TableCell>
                                    <Typography
                                      component="span"
                                      sx={styles.weekday}
                                    >
                                      {row.shift}
                                    </Typography>
                                  </TableCell>
                                  <TableCell />
                                  <TableCell />
                                  <TableCell />
                                  <TableCell />
                                  <HM_FormattedCell
                                    hours={row.devlogHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.leaveHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.trainingHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.billableHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.nonBillableHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.outageHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.netDevlogHrs ?? 0}
                                  />
                                  <Deviation_FormattedCell
                                    hours={row.deviationHours}
                                  />
                                </TableRow>
                              )
                            } else if (
                              row.date &&
                              row.workDuration === 'Work in Progress'
                            ) {
                              return (
                                <TableRow
                                  key={`${row.name}-${row.date}`}
                                  sx={
                                    themeStyles(isDarkMode).tableRowInProgress
                                  }
                                >
                                  <TableCell>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                      }}
                                    >
                                      <Typography sx={styles.employeeName}>
                                        {row.name}
                                      </Typography>
                                      <Typography sx={styles.employeeCol}>
                                        {row.employeeNo}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>{row.date}</TableCell>
                                  <TableCell>
                                    <Typography component="span">
                                      {row.shift}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{row.wfh}</TableCell>
                                  <TableCell>{row?.totalDuration}</TableCell>
                                  <TableCell>{row?.breakDuration}</TableCell>
                                  <TableCell>{row.workDuration}</TableCell>
                                  <HM_FormattedCell
                                    hours={row.devlogHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.leaveHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.trainingHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.billableHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.nonBillableHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.outageHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.netDevlogHrs ?? 0}
                                  />
                                  <Deviation_FormattedCell
                                    hours={row.deviationHours}
                                  />
                                </TableRow>
                              )
                            } else if (row.isWeeklyTotal) {
                              return (
                                <TableRow
                                  key={identity(`weekly-${index}`)}
                                  sx={
                                    themeStyles(isDarkMode).tableRowWeeklyTotal
                                  }
                                >
                                  <TableCell>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                      }}
                                    >
                                      <Typography sx={styles.employeeName}>
                                        {row.name}
                                      </Typography>
                                      <Typography sx={styles.employeeCol}>
                                        {row.employeeNo}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>{row.date}</TableCell>
                                  <TableCell colSpan={2}>
                                    <Typography
                                      component="div"
                                      sx={{
                                        fontWeight: 'bold',
                                        whiteSpace: 'pre-line',
                                      }}
                                    >
                                      {row.shift}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>
                                    {row.totalDuration}
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>
                                    {row.breakDuration}
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>
                                    {row.workDuration}
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>
                                    {hoursToHMFormat(row.devlogHours ?? 0)} (
                                    {row.devlogHours})
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>
                                    {hoursToHMFormat(row.leaveHours ?? 0)} (
                                    {row.leaveHours})
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>
                                    {hoursToHMFormat(row.trainingHours ?? 0)} (
                                    {row.trainingHours})
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>
                                    {hoursToHMFormat(row.billableHours ?? 0)} (
                                    {row.billableHours})
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>
                                    {hoursToHMFormat(row.nonBillableHours ?? 0)}{' '}
                                    ({row.nonBillableHours})
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>
                                    {hoursToHMFormat(row.outageHours ?? 0)} (
                                    {row.outageHours})
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>
                                    {hoursToHMFormat(row.netDevlogHrs ?? 0)} (
                                    {row.netDevlogHrs})
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>
                                    {hoursToHMFormat(row.deviationHours ?? 0)} (
                                    {row.deviationHours})
                                  </TableCell>
                                </TableRow>
                              )
                            } else if (row.date) {
                              return (
                                <TableRow
                                  key={`${row.name}-${row.date}`}
                                  sx={styles.tableRow}
                                >
                                  <TableCell
                                    sx={themeStyles(isDarkMode).whoisinCell}
                                  >
                                    <Link
                                      component="button"
                                      sx={styles.dateLink}
                                      onClick={() =>
                                        handleDateClick(
                                          row.userId || '',
                                          row.name || '',
                                          row.date,
                                        )
                                      }
                                    >
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          flexDirection: 'column',
                                        }}
                                      >
                                        <Typography sx={styles.employeeName}>
                                          {row.name}
                                        </Typography>
                                        <Typography sx={styles.employeeCol}>
                                          {row.employeeNo}
                                        </Typography>
                                      </Box>
                                    </Link>
                                  </TableCell>
                                  <TableCell
                                    sx={themeStyles(isDarkMode).whoisinCell}
                                  >
                                    {row.date}
                                  </TableCell>
                                  <TableCell
                                    sx={themeStyles(isDarkMode).whoisinCell}
                                  >
                                    <Box sx={styles.shiftCol}>{row.shift}</Box>
                                  </TableCell>
                                  <TableCell
                                    sx={themeStyles(isDarkMode).whoisinCell}
                                  >
                                    {row.wfh}
                                  </TableCell>
                                  <TableCell
                                    sx={themeStyles(isDarkMode).whoisinCell}
                                  >
                                    {row?.totalDuration}
                                  </TableCell>
                                  <TableCell
                                    sx={themeStyles(isDarkMode).whoisinCell}
                                  >
                                    {row?.breakDuration}
                                  </TableCell>
                                  <TableCell
                                    sx={themeStyles(isDarkMode).whoisinCell}
                                  >
                                    {row.workDurationInHrs
                                      ? `${row.workDuration} (${row?.workDurationInHrs})`
                                      : row.workDuration}
                                  </TableCell>
                                  <HM_FormattedCell
                                    hours={row.devlogHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.leaveHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.trainingHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.billableHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.nonBillableHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.outageHours ?? 0}
                                  />
                                  <HM_FormattedCell
                                    hours={row.netDevlogHrs ?? 0}
                                  />
                                  <Deviation_FormattedCell
                                    hours={row.deviationHours}
                                  />
                                </TableRow>
                              )
                            }
                            return (
                              <TableRow
                                key={identity(`tableEntry-${index}`)}
                                sx={{
                                  ...styles.tableRow,
                                  backgroundColor: isDarkMode
                                    ? 'rgba(79, 79, 79, 25%)'
                                    : 'rgba(79, 79, 79, 7.5%)',
                                }}
                              >
                                <TableCell sx={styles.boldText}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                    }}
                                  >
                                    <Typography sx={styles.employeeName}>
                                      {row.name}
                                    </Typography>
                                    <Typography sx={styles.employeeCol}>
                                      {row.employeeNo}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell sx={styles.boldText}>
                                  {row.date}
                                </TableCell>
                                <TableCell sx={styles.boldText}>
                                  <Typography
                                    component="span"
                                    sx={styles.shiftCounts}
                                  >
                                    {row.shift}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={styles.boldText}>
                                  {row.wfh}
                                </TableCell>
                                <TableCell sx={styles.boldText}>
                                  {row.totalDuration}
                                </TableCell>
                                <TableCell sx={styles.boldText}>
                                  {row.breakDuration}
                                </TableCell>
                                <TableCell sx={styles.boldText}>
                                  {hoursToHMFormat(row.workDurationInHrs ?? 0)}{' '}
                                  ({row.workDurationInHrs})
                                </TableCell>
                                <TableCell sx={styles.boldText}>
                                  {hoursToHMFormat(row.devlogHours ?? 0)} (
                                  {row.devlogHours})
                                </TableCell>
                                <TableCell sx={styles.boldText}>
                                  {hoursToHMFormat(row.leaveHours ?? 0)} (
                                  {row.leaveHours})
                                </TableCell>
                                <TableCell sx={styles.boldText}>
                                  {hoursToHMFormat(row.trainingHours ?? 0)} (
                                  {row.trainingHours})
                                </TableCell>
                                <TableCell sx={styles.boldText}>
                                  {hoursToHMFormat(row.billableHours ?? 0)} (
                                  {row.billableHours})
                                </TableCell>
                                <TableCell sx={styles.boldText}>
                                  {hoursToHMFormat(row.nonBillableHours ?? 0)} (
                                  {row.nonBillableHours})
                                </TableCell>
                                <TableCell sx={styles.boldText}>
                                  {hoursToHMFormat(row.outageHours ?? 0)} (
                                  {row.outageHours})
                                </TableCell>
                                <TableCell sx={styles.boldText}>
                                  {hoursToHMFormat(row.netDevlogHrs ?? 0)} (
                                  {row?.netDevlogHrs})
                                </TableCell>
                                <TableCell sx={styles.boldText}>
                                  {hoursToHMFormat(row.deviationHours ?? 0)} (
                                  {row.deviationHours})
                                </TableCell>
                              </TableRow>
                            )
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {!isLoading && reportsData?.length > 10 && (
                    <TablePagination
                      component="div"
                      count={reportsData.length}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  )}
                </Paper>
              ) : (
                <Box sx={styles.borderedBox}>
                  <Typography sx={styles.notFound} variant="h6">
                    No Reports Found
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </>
      )}
      {activeTab === 1 && (
        <Box>
          <LocationReportFilter
            loading={locationReportLoading}
            onApply={handleLocationReport}
          />
          {!locationReportLoading && locationReportData.length > 0 && (
            <LocationReportDisplay
              data={locationReportData}
              isLoading={locationReportLoading}
            />
          )}
          {locationReportLoading && (
            <Box sx={styles.loader}>
              <CircularProgress size={27} />
              <Typography>Loading location report...</Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  ) : (
    <Navigate to={Path.Root} />
  )
}

const commonTableRowStyle = {
  '&:last-child td, &:last-child th': { border: 0 },
}
const styles = {
  headerRow: {
    'th, th span, th p': {
      fontSize: '0.7rem',
    },
  },
  employeeCol: {
    fontSize: '0.7rem',
    textAlign: 'left',
  },
  employeeName: {
    fontSize: '0.7rem',
    clear: 'both',
    display: 'inline-block',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  root: { p: 0.5, mt: 2.5, ml: '1rem', mr: '1rem' },
  boldText: { fontSize: '0.875rem', fontWeight: 'bold' },
  varianceCell: {
    backgroundColor: '#c62828',
  },
  tableRow: {
    position: 'relative',
    ...commonTableRowStyle,
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1rem',
    marginTop: '1rem',
  },
  downloadIcon: {
    p: 0,
  },
  weekday: {
    position: 'absolute',
    display: 'flex',
    gap: '8px',
    top: '32%',
    left: '30%',
  },
  shiftCounts: {
    fontWeight: 'bold',
    position: 'absolute',
    top: '32%',
    left: '16px',
  },
  borderedBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: '2px solid #bdbdbd',
    mt: '1.5rem',
    p: '1.5rem',
  },
  notFound: {
    display: 'flex',
    justifyContent: 'center',
  },
  headerText: {
    fontWeight: 'bold',
  },
  headerSubText: {
    fontSize: '11px',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  },
  dateLink: {
    cursor: 'pointer',
    textDecoration: 'none',
    fontWeight: 'inherit',
    color: (theme: Theme) =>
      theme.palette.mode === 'dark' ? '#0b295b' : '#357bea',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  shiftCol: {
    clear: 'both',
    display: 'inline-block',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
}

const themeStyles = (isDarkMode: boolean) => ({
  table: {
    marginBottom: '32px',
    border: '0.031rem solid',
    borderColor: isDarkMode ? 'rgba(255, 255, 255, 10%)' : 'rgba(0, 0, 0, 10%)',
  },
  tableRowWeekday: {
    position: 'relative',
    ...commonTableRowStyle,
    backgroundColor: isDarkMode ? '#989675' : '#fff8e1',
  },
  tableRowLeave: {
    position: 'relative',
    ...commonTableRowStyle,
    backgroundColor: isDarkMode ? '#936368' : '#ffcdd2',
  },
  rowPendingLeave: {
    ...commonTableRowStyle,
    position: 'relative',
    backgroundColor: isDarkMode
      ? 'rgba(218,186,193,0.61)'
      : 'rgba(252,229,232,0.86)',
  },
  tableRowHoliday: {
    ...commonTableRowStyle,
    position: 'relative',
    backgroundColor: isDarkMode ? '#749179' : '#e7ffcd',
  },
  tableRowShift: {
    ...commonTableRowStyle,
    position: 'relative',
    backgroundColor: isDarkMode ? 'rgba(42,42,42,0.26)' : '#fafafa',
  },
  tableRowInProgress: {
    ...commonTableRowStyle,
    position: 'relative',
    backgroundColor: (theme: Theme) => theme.palette.error.main,
    td: {
      backgroundColor: (theme: Theme) => theme.palette.error.main,
    },
  },
  tableRowWeeklyTotal: {
    ...commonTableRowStyle,
    position: 'relative',
    backgroundColor: '#F3BB70',
    borderTop: '1px dashed #90caf9',
    borderBottom: '1px dashed #90caf9',
    fontWeight: 'bold',
  },
  tableHeader: {
    fontSize: '0.9rem',
    backgroundColor: isDarkMode ? 'rgba(91, 153, 185)' : '#81d4fa',
  },
  tableHeaderLogs: {
    fontSize: '0.9rem',
    backgroundColor: isDarkMode ? '#888c15' : '#fff176',
  },
  tableHeaderVariance: {
    fontSize: '0.9rem',
    backgroundColor: '#c62828',
    color: 'white',
  },
  whoisinCell: {
    backgroundColor: isDarkMode ? 'rgba(91,153,185,0.77)' : '#81d4fa',
  },
  timesheetCell: {
    backgroundColor: isDarkMode ? '#888c15' : '#fff176',
  },
})

export default Reports
